import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import css from '@styled-system/css';
import { useRouter } from 'next/router';

import { contentfulMediaType } from 'types';
import { track } from 'utils/analytics';
import Section from 'legacy/components/section';
import Flex from 'legacy/components/flex';
import Box from 'legacy/components/box';
import Heading from 'legacy/components/heading';
import Button from 'legacy/components/button';
import ResourcePreview from 'legacy/components/lcResourcePreview';

const ResourceWrapper = styled(Box)(
  css({
    mt: [5, null, null, 6],
    width: ['100%', null, null, '33.33333333%'],
    px: 3,
  })
);

const ResourceList = ({
  heading,
  resources,
  showLoadMore = false,
  showResourcePill = false,
  pillContentStyle = 'contentType',
  initialShownCount = 3,
  ...props
}) => {
  const { asPath } = useRouter();
  const [shownResources, setShownResources] = useState(
    resources.slice(0, initialShownCount)
  );

  const showAll = resources.length === shownResources.length;

  // Check when props updates to see if the resources list has changed.
  useEffect(() => {
    setShownResources(resources);
  }, [resources]);

  const handleLoadMoreClick = () => {
    setShownResources(resources);

    track('clicked_load_more', {
      location: asPath,
    });
  };

  return (
    <Section {...props}>
      <Section.Inner>
        <Box mt={[5, null, null, 0]} width={1}>
          <Heading as="h2" variant={['h4', null, null, 'h3']} textAlign="center">
            {heading}
          </Heading>
          <Flex flexWrap="wrap" mx={-3}>
            {shownResources.map((resource, index) => (
              <ResourceWrapper
                // eslint-disable-next-line react/no-array-index-key
                key={index}
              >
                <ResourcePreview
                  resource={resource}
                  showResourcePill={showResourcePill}
                  pillContent={
                    pillContentStyle === 'contentType'
                      ? null
                      : resource.subcategoriesCollection?.items?.[0]?.title
                  }
                />
              </ResourceWrapper>
            ))}
          </Flex>
          {!showAll && showLoadMore && (
            <Flex justifyContent="center" width={1} mt={[5, null, null, 6]}>
              <Button variant="outline" btnSize="sm" onClick={handleLoadMoreClick}>
                Load More
              </Button>
            </Flex>
          )}
        </Box>
      </Section.Inner>
    </Section>
  );
};

ResourceList.propTypes = {
  heading: PropTypes.string.isRequired,
  resources: PropTypes.arrayOf(
    PropTypes.oneOf([
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        image: contentfulMediaType.isRequired,
        slug: PropTypes.string,
        url: PropTypes.string,
      }),
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ])
  ).isRequired,
  showLoadMore: PropTypes.bool,
  showResourcePill: PropTypes.bool,
  pillContentStyle: PropTypes.oneOf(['contentType', 'subcategory']),
  initialShownCount: PropTypes.number,
};

export default ResourceList;
